import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"

import { Strings } from "--root/strings/en";

import { Button } from "--floponents-web/atoms/Button";

import { AVDevices } from "--client/common/TypedStore";
import { useTypedStore } from "--client/common/apis/hooks/useTypedStore";

import { DeviceSelect, DeviceSelectContainer } from "./DeviceSelect";
import { destroyMediaStream, getUserAudioStream, getUserVideoStream, isSetSinkIdSupported } from "./utils";
import { MicrophoneSettings } from "./MicrophoneSettings";
import { CameraSettings } from "./CameraSettings";

// Add missing setSinkId method typedef
declare global {
    interface HTMLAudioElement {
        setSinkId?(deviceId?: string): void
    }
}

const StyledAVSettings = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing(1)};
    flex: 1;
`;

export const AVSettings = () => {
    const storedDevices = useTypedStore(AVDevices);

    const [audioMediaStream, setAudioStream] = useState<MediaStream>();
    const [videoMediaStream, setVideoStream] = useState<MediaStream>();

    const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>();

    // Get microphone stream
    useEffect(() => {
        async function updateOutputDevices() {
            setOutputDevices(
                (await navigator.mediaDevices.enumerateDevices())
                    .filter(device => device.kind === "audiooutput")
            );
        }
        navigator.mediaDevices.addEventListener("devicechange", updateOutputDevices);

        const audioStreamPromise = getUserAudioStream(storedDevices?.micId);
        audioStreamPromise
            .then(async stream => {
                setAudioStream(stream);
                await updateOutputDevices();
            })
            .catch(() => { /* empty */ });

        return () => {
            navigator.mediaDevices.removeEventListener("devicechange", updateOutputDevices);
            audioStreamPromise
                .then(destroyMediaStream)
                .catch(() => { /* empty */ });
        };
    }, [storedDevices?.micId]);

    // Get camera stream
    useEffect(() => {
        const videoStreamPromise = getUserVideoStream(storedDevices?.webcamId);
        videoStreamPromise
            .then(setVideoStream)
            .catch(() => { /* empty */ });

        return () => {
            videoStreamPromise
                .then(destroyMediaStream)
                .catch(() => { /* empty */ });
        };

    }, [storedDevices?.webcamId]);

    const setSinkIdSupported = isSetSinkIdSupported();
    const speakerAudioRef = useRef<HTMLAudioElement>(null);

    const testAudioOutput = useCallback(() => {
        try {
            speakerAudioRef.current?.setSinkId?.(storedDevices?.speakerId);
            speakerAudioRef.current?.play();
        } catch { /* empty */ }
    }, [storedDevices?.speakerId]);
    const testRingtoneAudioOutput = useCallback(() => {
        try {
            speakerAudioRef.current?.setSinkId?.(storedDevices?.ringtoneSpeakerId);
            speakerAudioRef.current?.play();
        } catch { /* empty */ }
    }, [storedDevices?.ringtoneSpeakerId]);

    const onAudioOutDeviceChanged = useCallback((deviceId: string) => {
        AVDevices.set({ ...storedDevices, speakerId: deviceId })
    }, [storedDevices]);
    const onRingtoneAudioOutputDeviceChanged = useCallback((deviceId: string) => {
        AVDevices.set({ ...storedDevices, ringtoneSpeakerId: deviceId })
    }, [storedDevices]);

    return (
        <StyledAVSettings>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio ref={speakerAudioRef} src="/apps/assets/tones/ringtone.ogg" />
            <MicrophoneSettings mediaStream={audioMediaStream} />
            <CameraSettings mediaStream={videoMediaStream} />

            {setSinkIdSupported && <>
                <DeviceSelectContainer>
                    <DeviceSelect
                        placeholder={Strings.audioVideoSettings.audioOutputPlaceholder}
                        disabled={!outputDevices?.length}
                        devices={outputDevices}
                        deviceId={storedDevices?.speakerId ?? "default"}
                        label={Strings.audioVideoSettings.callAudioOutput}
                        onChange={onAudioOutDeviceChanged}
                    />
                    <Button
                        size="medium"
                        color="grey"
                        onClick={testAudioOutput}
                        title={Strings.audioVideoSettings.testCallAudioOutput}
                    >
                        {Strings.audioVideoSettings.test}
                    </Button>
                </DeviceSelectContainer>

                <DeviceSelectContainer>
                    <DeviceSelect
                        placeholder={Strings.audioVideoSettings.audioOutputPlaceholder}
                        disabled={!outputDevices?.length}
                        devices={outputDevices}
                        deviceId={storedDevices?.ringtoneSpeakerId ?? "default"}
                        label={Strings.audioVideoSettings.ringtoneAudioOutput}
                        onChange={onRingtoneAudioOutputDeviceChanged}
                    />
                    <Button
                        size="medium"
                        color="grey"
                        onClick={testRingtoneAudioOutput}
                        title={Strings.audioVideoSettings.testRingtoneAudioOutput}
                    >
                        {Strings.audioVideoSettings.test}
                    </Button>
                </DeviceSelectContainer>
            </>}
        </StyledAVSettings>
    );
};
